
import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Clock, SidebarOpen, Circle } from 'lucide-react';
import { formatDuration } from '@/utils/formatTime';
import { useIsMobile } from '@/hooks/use-mobile';

interface LiveSessionHeaderProps {
  sessionName: string;
  projectName: string;
  isRecording: boolean;
  elapsedTime: number;
  sessionDuration: number;
  onLeave: () => void;
  onToggleRecording: () => void;
  onToggleSidebar: () => void;
}

const LiveSessionHeader: React.FC<LiveSessionHeaderProps> = ({
  sessionName,
  projectName,
  isRecording,
  elapsedTime,
  sessionDuration,
  onLeave,
  onToggleRecording,
  onToggleSidebar
}) => {
  const isMobile = useIsMobile();
  
  return (
    <div className="p-4 flex items-center justify-between bg-black/40 backdrop-blur-sm border-b border-[#403E43]/40 sticky top-0 z-40">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onLeave}
          className="text-white hover:text-white hover:bg-white/10"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className={isMobile ? "max-w-[150px]" : ""}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-white truncate">{sessionName}</h1>
            <Badge variant="outline" className={`${isRecording ? 'bg-red-500/20 text-red-400 border-red-500/50 animate-pulse' : 'bg-green-500/20 text-green-400 border-green-500/50'}`}>
              {isRecording ? 'RECORDING' : 'LIVE'}
            </Badge>
          </div>
          <p className="text-sm text-white/60 truncate">
            Project: {projectName || 'Unknown Project'}
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 bg-black/50 px-3 py-1 rounded-full border border-[#403E43]/40">
          <Clock className="h-4 w-4 text-white/70" />
          <span className="text-sm text-white">
            {isRecording 
              ? formatDuration(elapsedTime) 
              : formatDuration(Math.floor(sessionDuration * 60))}
          </span>
        </div>
        
        {!isMobile && (
          <Button 
            variant={isRecording ? "destructive" : "outline"}
            size="sm"
            onClick={onToggleRecording}
            className={`gap-1 ${isRecording ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/50' : 'bg-black/50 text-white hover:bg-white/10 border-[#403E43]/40'}`}
          >
            <Circle className={`h-4 w-4 ${isRecording ? 'animate-pulse' : ''}`} />
            <span>{isRecording ? 'Stop Recording' : 'Start Recording'}</span>
          </Button>
        )}
        
        {isMobile && (
          <Button
            variant="default"
            size="icon"
            onClick={onToggleSidebar}
            className="text-white bg-soundcharts-purple hover:bg-soundcharts-purple/80 relative flex items-center justify-center animate-pulse"
          >
            <SidebarOpen className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full"></span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default LiveSessionHeader;
